import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Alert,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { ErrorDisplay } from "../../components/common/ErrorDisplay";

// Form validation schema
const emailTestSchema = z.object({
  testEmail: z.string().email("Please enter a valid email address"),
  testType: z.enum(["simple", "template"]),
});

type EmailTestForm = z.infer<typeof emailTestSchema>;

/**
 * Admin page for testing email configuration
 */
const EmailTestingPage: React.FC = () => {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Get email configuration
  // @ts-ignore - Type checking for tRPC
  const { data: emailConfig, isLoading: configLoading } =
    trpc.admin.getEmailConfig.useQuery();

  // Test email mutation
  // @ts-ignore - Type checking for tRPC
  const { mutateAsync: sendTestEmail, isLoading: sendingEmail } =
    trpc.admin.testEmail.useMutation();

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EmailTestForm>({
    resolver: zodResolver(emailTestSchema),
    defaultValues: {
      testEmail: "",
      testType: "template",
    },
  });

  // Handle form submission
  const onSubmit = async (data: EmailTestForm) => {
    try {
      await sendTestEmail({
        testEmail: data.testEmail,
        testType: data.testType,
      });

      setNotification({
        open: true,
        message: `Test email sent successfully to ${data.testEmail}!`,
        severity: "success",
      });

      // Clear form on success
      reset();
    } catch (error) {
      setNotification({
        open: true,
        message: `Failed to send test email: ${error instanceof Error ? error.message : "Unknown error"}`,
        severity: "error",
      });
    }
  };

  // Close notification
  const closeNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
        >
          <EnvelopeIcon className="h-8 w-8" />
          Email Testing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test your email configuration by sending test emails and viewing
          current settings.
        </Typography>
      </Box>

      {/* Notification */}
      {notification.open && (
        <Alert
          severity={notification.severity}
          onClose={closeNotification}
          sx={{ mb: 3 }}
        >
          {notification.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Email Configuration Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Current Email Configuration"
              subheader="View your current email service settings"
            />
            <CardContent>
              {configLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress />
                </Box>
              ) : emailConfig ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          Provider
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emailConfig.provider}
                            color={
                              emailConfig.provider.includes("Mailgun")
                                ? "primary"
                                : "secondary"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          From Address
                        </TableCell>
                        <TableCell>{emailConfig.fromAddress}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          Environment
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emailConfig.environment}
                            color={
                              emailConfig.environment === "production"
                                ? "success"
                                : "warning"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          SMTP Host
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emailConfig.smtp.host}
                            color={
                              emailConfig.smtp.host === "Configured"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          Mailgun Domain
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emailConfig.mailgun.domain}
                            color={
                              emailConfig.mailgun.domain === "Configured"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: "bold" }}
                        >
                          Mailgun API Key
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={emailConfig.mailgun.apiKey}
                            color={
                              emailConfig.mailgun.apiKey === "Configured"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <ErrorDisplay error="Failed to load email configuration" />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Test Email Form Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Send Test Email"
              subheader="Test your email configuration by sending a test email"
            />
            <CardContent>
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                {/* Email Address Input */}
                <Controller
                  name="testEmail"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Test Email Address"
                      type="email"
                      fullWidth
                      required
                      error={!!errors.testEmail}
                      helperText={errors.testEmail?.message}
                      placeholder="Enter email address to send test to"
                    />
                  )}
                />

                {/* Email Type Selection */}
                <FormControl component="fieldset">
                  <FormLabel component="legend">Email Type</FormLabel>
                  <Controller
                    name="testType"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel
                          value="template"
                          control={<Radio />}
                          label="Template (HTML with styling)"
                        />
                        <FormControlLabel
                          value="simple"
                          control={<Radio />}
                          label="Simple (Plain text)"
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={sendingEmail}
                  startIcon={
                    sendingEmail ? (
                      <CircularProgress size={20} />
                    ) : (
                      <EnvelopeIcon className="h-5 w-5" />
                    )
                  }
                  sx={{ mt: 1 }}
                >
                  {sendingEmail ? "Sending Test Email..." : "Send Test Email"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Instructions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📧 How to Use Email Testing
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Template Email:</strong> Sends a beautiful HTML email with
            your app branding, configuration details, and success indicators.
            This is the recommended option for testing the full email
            experience.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Simple Email:</strong> Sends a plain text email with basic
            configuration information. Useful for testing basic connectivity
            when HTML rendering might be an issue.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Troubleshooting:</strong> If emails aren't being received,
            check your spam folder, verify the email configuration above, and
            check the server logs for detailed error messages.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailTestingPage;
